import * as React from 'react';

import * as ramda from 'ramda';

import type {RelationshipFilter} from 'nutshell-core/deserialize-relationship';
import {deserializeRelationship} from 'nutshell-core/deserialize-relationship';
import * as Schema from 'nutshell-core/schema';
import {serialize} from 'nutshell-core/serialize';
import type {EntityListType, FilterObject} from 'nutshell-core/types';

import {FilterDivider} from './filter-divider';
import {FilterSection} from './filter-section';
import {MultipleFilterSection} from './multiple-filter-section';

import styles from './filter-section.module.css';

type Props = {
    entityType: EntityListType;
    field: Schema.EnumField | Schema.RelationshipField | Schema.StageField;
    filter: FilterObject | null | undefined;
    isOpen: boolean;
    allowNegation: boolean;
    onOpenSection: (filterName: string) => any;
    onCloseSection: (filterName: string) => any;
    onSubmitFilter: (
        filterObj: FilterObject,
        field: Schema.EnumField | Schema.RelationshipField | Schema.StageField
    ) => void;
    shownColumns?: string[];
    updateShownColumns?: (arg1: string[], arg2: boolean) => void;
};
type State = {
    isHoveringAndOr: boolean;
    filterSectionCount: number;
    unappliedFilterIsNot: boolean;
};

const DEFAULT_ISNOT = false;
const DEFAULT_ANYALL = 'any';
const DEFAULT_FILTER_ITEM = {isNot: DEFAULT_ISNOT, data: ''} as const;

export class MultipleFilter extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props);
        const filter = this.getFilter(props.field, props.filter);
        this.state = {
            isHoveringAndOr: false,
            filterSectionCount: (filter && filter.data.length) || 1,
            unappliedFilterIsNot: DEFAULT_ISNOT,
        };
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        if (nextProps.isOpen && !this.props.isOpen) {
            this.setState({
                filterSectionCount:
                    (nextProps.filter && nextProps.filter.data && nextProps.filter.data.length) ||
                    1,
            });
        }
    }

    render() {
        const {field} = this.props;

        // Generate a filter object based on our already applied filter (or
        // get an default empty one if we're dealing with a new filter)
        const filterObj: RelationshipFilter | null | undefined = this.getFilter();
        const appliedFilters = filterObj ? filterObj.data : [];
        if (appliedFilters.length < this.state.filterSectionCount) {
            const unappliedFilter = ramda.clone(DEFAULT_FILTER_ITEM);
            // @ts-expect-error
            unappliedFilter.isNot = this.state.unappliedFilterIsNot;
            appliedFilters.push(unappliedFilter);
        }

        return (
            <FilterSection
                field={field}
                filter={this.props.filter}
                isOpen={this.props.isOpen}
                onOpenSection={this.props.onOpenSection}
                onCloseSection={this.props.onCloseSection}
                onAddFilter={this.handleAddFilter}
                shownColumns={this.props.shownColumns}
                updateShownColumns={this.props.updateShownColumns}
            >
                {appliedFilters.map((filter, index) => {
                    return (
                        <div
                            key={`${field.name}-${index}`}
                            className={styles['multi-form-container']}
                        >
                            {index !== 0 ? (
                                <FilterDivider
                                    isAndable={field.isAndable}
                                    anyAll={(filterObj && filterObj.anyAll) || DEFAULT_ANYALL}
                                    onMouseOver={() => this.handleAndOrHover(true)}
                                    onMouseOut={() => this.handleAndOrHover(false)}
                                    isHovered={this.state.isHoveringAndOr}
                                    onClick={this.handleAndOrClick}
                                />
                            ) : undefined}
                            <MultipleFilterSection
                                field={field}
                                filter={filter}
                                entityType={this.props.entityType}
                                allowNegation={this.props.allowNegation}
                                sectionIndex={index}
                                totalSections={appliedFilters.length}
                                excludedOptions={appliedFilters.map(
                                    (appliedFilter) => appliedFilter.data
                                )}
                                onRemoveFilter={this.handleRemoveFilter(index)}
                                onClearFilter={this.handleClearFilter(index)}
                                onFilterChange={this.handleChange(index)}
                                onNegationChange={this.handleNegationChange(index)}
                                onFilterNegationChange={this.handleFilterChange(index)}
                            />
                        </div>
                    );
                })}
            </FilterSection>
        );
    }

    getFilter = (
        field: Schema.EnumField | Schema.RelationshipField | Schema.StageField = this.props.field,
        // @ts-expect-error
        filter: FilterObject | null = this.props.filter
    ): RelationshipFilter | null | undefined => {
        if (filter) {
            const filterObj = ramda.clone(filter);

            return deserializeRelationship(filterObj[field.name]);
        }
    };

    handleAndOrHover = (isHovering: boolean) => {
        this.setState({isHoveringAndOr: isHovering});
    };

    handleAndOrClick = () => {
        const filter = this.getFilter();
        if (filter) {
            filter.anyAll = filter.anyAll === 'any' ? 'all' : 'any';
            this.updateFilter(filter);
        }
    };

    handleAddFilter = () => {
        this.setState((state) => ({filterSectionCount: state.filterSectionCount + 1}));
    };

    /**
     * This will return a function that accepts a new value to be applied at
     * a given index in the filter's `data` array.  If the index already
     * exists, the value at that index will be changed.  If it does not already
     * exist, this means that a new filter is being added, so we will create
     * a new data object using the value provided.  We will also use the
     * `isNot` that is stored in state for just such an occation.
     *
     * @param  {number} index  The `data` index to change
     * @return {Function}      A function to change the data at the provided index
     */
    handleChange = (index: number) => (newValue: string) => {
        let filter = this.getFilter();
        if (filter && filter.data[index]) {
            // Modify existing filter data
            filter.data[index].data = newValue;
        } else if (filter) {
            // Add a new `data` item
            filter = {
                anyAll: filter.anyAll,
                data: [
                    ...filter.data,
                    {
                        isNot: this.state.unappliedFilterIsNot,
                        data: newValue,
                    },
                ],
            };
        } else {
            // Adding the intial filter
            filter = {
                anyAll: DEFAULT_ANYALL,
                data: [
                    {
                        isNot: this.state.unappliedFilterIsNot,
                        data: newValue,
                    },
                ],
            };
        }

        this.updateFilter(filter);
    };

    /**
     * Create a function which will update the `isNot` at a given index of the
     * filter's `data` array.  If the index already exists, the `isNot` will
     * be changed and the new resulting filter will be applied.  If it does't
     * exist, that means the user changed the isNot for a filter which hasn't
     * been applied yet, so we will just save this state and use it when
     * a value is provided for this unapplied filter.  This prevents us from
     * having to fire requests every time isNot is changed on a filter which
     * isn't having any effect yet.
     *
     * @param  {number} index  The `data` index to change
     * @return {Function}      A function to change the isNot at the provided index
     */
    handleNegationChange = (index: number) => (value: boolean) => {
        // If value comes back as the boolean 'false', we're considering
        // that "true" for `isNot`
        const isNot = value === false;
        const filter = this.getFilter();
        if (filter && filter.data[index]) {
            // Modify existing filter isNot
            filter.data[index].isNot = isNot;
            this.updateFilter(filter);
        } else {
            // The isNot was changed for a filter which has not yet
            // actually been applied
            this.setState({unappliedFilterIsNot: isNot});
        }
    };

    /**
     * This will return a function that accepts a new value to be applied at
     * a given index in the filter's `data` array.  If the index already
     * exists, the value at that index will be changed.  If it does not already
     * exist, this means that a new filter is being added, so we will create
     * a new data object using the value provided.  We will also use the
     * `isNot` that is stored in state for just such an occation.
     *
     * @param  {number} index  The `data` index to change
     * @return {Function}      A function to change the data at the provided index
     */
    handleFilterChange = (index: number) => (newFilterValue: string, newNegationValue: boolean) => {
        // If value comes back as the boolean 'false', we're considering
        // that "true" for `isNot`
        const isNot = newNegationValue === false;
        let filter = this.getFilter();
        if (filter && filter.data[index]) {
            // Modify existing filter isNot
            filter.data[index].isNot = isNot;
        } else {
            // The isNot was changed for a filter which has not yet
            // actually been applied
            this.setState({unappliedFilterIsNot: isNot});
        }

        if (filter && filter.data[index]) {
            // Modify existing filter data
            filter.data[index].data = newFilterValue;
        } else if (filter) {
            // Add a new `data` item
            filter = {
                anyAll: filter.anyAll,
                data: [
                    ...filter.data,
                    {
                        isNot: isNot,
                        data: newFilterValue,
                    },
                ],
            };
        } else {
            // Adding the intial filter
            filter = {
                anyAll: DEFAULT_ANYALL,
                data: [
                    {
                        isNot: isNot,
                        data: newFilterValue,
                    },
                ],
            };
        }

        this.updateFilter(filter);
    };

    handleClearFilter = (index: number) => () => {
        const filter = this.getFilter();
        if (filter) {
            // @ts-expect-error
            filter.data[index].data = '';
            this.updateFilter(filter);
        }
    };

    handleRemoveFilter = (index: number) => () => {
        const filter = this.getFilter();
        if (filter) {
            filter.data.splice(index, 1); // Remove selected index from data list
            this.updateFilter(filter);
            // Decrement the count of active filter sections
            this.setState((state) => ({filterSectionCount: state.filterSectionCount - 1}));
        } else {
            this.props.onCloseSection(this.props.field.name);
        }
    };

    updateFilter = (newFilter?: RelationshipFilter | null) => {
        const field = this.props.field;
        if (!newFilter || !newFilter.data.length) {
            this.props.onCloseSection(field.name);
        } else {
            const dataToSerialize = {[field.name]: newFilter} as const;
            const serializedFilterObject = serialize(dataToSerialize, field.type);
            this.props.onSubmitFilter(serializedFilterObject, field);
        }
    };
}
